import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { MatAutocomplete } from '@angular/material/autocomplete';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  ActivatedRoute,
  NavigationStart,
  Router,
  RouterLink
} from '@angular/router';
import {
  BehaviorSubject,
  ReplaySubject,
  Subject,
  combineLatest,
  filter,
  timer as observableTimer,
  of
} from 'rxjs';
import { DepotLocation, Docket, HireItem, Product } from '../models/';

import { ScrollingModule } from '@angular/cdk/scrolling';
import {
  AsyncPipe,
  DecimalPipe,
  NgClass,
  NgIf,
  PercentPipe
} from '@angular/common';
import { FlexModule } from '@angular/flex-layout';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCard, MatCardContent, MatCardTitle } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MomentModule } from 'ngx-moment';
import { ToastrService } from 'ngx-toastr';
import {
  map,
  mergeMap,
  skip,
  startWith,
  take,
  takeUntil,
  tap,
  shareReplay
} from 'rxjs/operators';
import { Consumable, User } from '../models';
import { RetailItemSale } from '../models/retail-item-sale';
import {
  ConfirmDialogComponent,
  DocketService,
  HireItemService,
  ProductService
} from '../shared/';
import { AlertTextComponent } from '../shared/alert-text/alert-text.component';
import { CommentsAndEmailsComponent } from '../shared/comments-and-emails/comments-and-emails.component';
import { CommentsComponent } from '../shared/comments/comments.component';
import { DocumentContactComponent } from '../shared/contact-management/document-contact/document-contact.component';
import { DeliveryListItem } from '../shared/deliveries/docket-delivery-list/deliverable-service.interface';
import {
  DELIVERABLE_SERVICE,
  DocketDeliveryListComponent
} from '../shared/deliveries/docket-delivery-list/docket-delivery-list.component';
import { DiscountDialogComponent } from '../shared/dialogs/discount-dialog/discount-dialog.component';
import { DocketSettingsDialogComponent } from '../shared/dialogs/docket-settings-dialog/docket-settings-dialog.component';
import { EmailDocumentToCustomerDialogComponent } from '../shared/dialogs/email-document-to-customer-dialog/email-document-to-customer-dialog.component';
import { base64ToBlob } from '../shared/helpers/base64ToBlob';
import { HireItemListStoreService } from '../shared/hire-items-list/hire-item-list-v2/hire-item-list-store.service';
import { HireItemListV2Component } from '../shared/hire-items-list/hire-item-list-v2/hire-item-list-v2.component';
import { IntegrationRelationshipInfoComponent } from '../shared/integration-relationship-info/integration-relationship-info.component';
import { BookingItemNamePipe } from '../shared/pipes/booking-item-name.pipe';
import { CustomerAddressPipe } from '../shared/pipes/customer-address.pipe';
import { CustomerContactDetailsPipe } from '../shared/pipes/customer-contact-details.pipe';
import { CustomerNamePipe } from '../shared/pipes/customer-name.pipe';
import { DeliveryItemNamePipe } from '../shared/pipes/delivery-item-name.pipe';
import {
  Chargeable,
  PriceContainerV2Component
} from '../shared/price-container-v2/price-container-v2.component';
import { RetailItemListComponent } from '../shared/retail-item-list/retail-item-list.component';
import {
  RetailItemStoreService,
  SALE_ITEM_SERVICE
} from '../shared/retail-item-list/retail-item.store.service';
import { AbstractPaymentService } from '../shared/services/abstract-payment.service';
import { ConfigService } from '../shared/services/config.service';
import { ContactService } from '../shared/services/contact.service';
import { DocketRetailItemSaleService } from '../shared/services/docket-retail-item-sale.service';
import { HireDocketPaymentService } from '../shared/services/hire-docket-payment.service';
import { SignatureSectionComponent } from '../shared/signature-section/signature-section.component';
import { UpdateCustomerDialogComponent } from '../shared/update-customer-dialog/update-customer-dialog.component';
import { RoundPriceDialogComponent } from './round-price-dialog/round-price-dialog.component';
@Component({
  selector: 'app-docket',
  templateUrl: './docket.component.html',
  styleUrls: ['./docket.component.scss'],
  providers: [
    DocketService,
    ProductService,
    HireItemService,
    { provide: AbstractPaymentService, useClass: HireDocketPaymentService },
    RetailItemStoreService,
    { provide: SALE_ITEM_SERVICE, useClass: DocketRetailItemSaleService },
    { provide: DELIVERABLE_SERVICE, useClass: DocketService },
    ContactService,
    BookingItemNamePipe,
    DeliveryItemNamePipe,
    HireItemListStoreService
  ],
  standalone: true,
  imports: [
    FlexModule,
    NgIf,
    AlertTextComponent,
    RouterLink,
    MatCard,
    MatCardContent,
    MatIconButton,
    MatTooltip,
    MatIcon,
    DocumentContactComponent,
    IntegrationRelationshipInfoComponent,
    MatButton,
    HireItemListV2Component,
    NgClass,
    ExtendedModule,
    MatCardTitle,
    RetailItemListComponent,
    SignatureSectionComponent,
    DocketDeliveryListComponent,
    PriceContainerV2Component,
    CommentsComponent,
    AsyncPipe,
    DecimalPipe,
    PercentPipe,
    CustomerNamePipe,
    CustomerContactDetailsPipe,
    CustomerAddressPipe,
    MomentModule,
    ScrollingModule,
    CommentsAndEmailsComponent
  ]
})
export class DocketComponent implements OnInit, OnDestroy {
  docket: Docket;
  products: Array<Product> = [];
  consumables: Consumable[];
  dialogRef: MatDialogRef<DocketSettingsDialogComponent>;
  confirmDialogRef: MatDialogRef<ConfirmDialogComponent>;
  deleteProcessing = false;
  currentLocation: DepotLocation;
  salesOrderLoading = false;
  currentUser: User;
  @ViewChild('printFrame') printFrame;
  @ViewChild(PriceContainerV2Component) priceContainer;
  @ViewChild(MatAutocomplete) consumableAutoComplete;
  @ViewChild(HireItemListV2Component) hireItemList;
  private ngUnsubscribe = new Subject<void>();
  possibleStatusUpdate = new Subject<void>();
  statusHeading = '';
  dateFormat = ConfigService.dates.dateFormat;
  dateTimeFormat = ConfigService.dates.longDateTimeFormat;
  defaultCurrency = 'GBP';
  hireItems: HireItem[] = [];
  hireItemsOfTypeItem: HireItem[] = [];
  taxRate$ = new BehaviorSubject<number>(0);
  private hireItems$ = new ReplaySubject<HireItem[]>(1);
  private deliveryChargeSource = new BehaviorSubject<DeliveryListItem[]>([]);

  private hireItemsChargeableSource = combineLatest([
    this.hireItems$,
    this.taxRate$
  ]).pipe(
    map(([hireItems, taxRate]) =>
      hireItems.map((item) => this.hireItemToChargeable(item, taxRate))
    )
  );

  chargeables$ = combineLatest([
    this.retailItemStoreService.items$.pipe(
      map((items) => items.map((item) => this.retailItemToChargeable(item)))
    ),
    this.hireItemsChargeableSource
  ]).pipe(
    map(([retailItems, hireItems]) => [...retailItems, ...hireItems]),
    startWith([])
  );
  deliveryCharges$ = combineLatest([
    this.deliveryChargeSource,
    this.taxRate$
  ]).pipe(
    map(([deliveries, taxRate]) =>
      deliveries.map((delivery) => this.deliveryToChargeable(delivery, taxRate))
    )
  );
  showConsumables = false;
  consumableCount$ = this.retailItemStoreService.items$.pipe(
    map((items) => items.length),
    startWith(0),
    tap((count) => (this.showConsumables = count > 0))
  );

  private docketId = new BehaviorSubject<number | null>(null);
  emailListSource$ = this.docketId.pipe(
    map((id) => ({ resource: 'docket', id }))
  );

  private isInvoiceAndClosed = new BehaviorSubject<boolean>(true);
  isDocketLocked$ = this.isInvoiceAndClosed.pipe(
    mergeMap((isClosed) => {
      return this.configService.userConfig.pipe(
        take(1),
        map(
          (config) => config['docket_locked_after_invoice'] === true && isClosed
        )
      );
    }),
    shareReplay(1)
  );

  refreshDataDependentOnHireItems$ =
    this.hireItemListStoreService.hireItemsChanged$;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private docketService: DocketService,
    public dialog: MatDialog,
    private configService: ConfigService,
    private customerNamePipe: CustomerNamePipe,
    private toastrService: ToastrService,
    private viewContainerRef: ViewContainerRef,
    private retailItemStoreService: RetailItemStoreService,
    private hireItemListStoreService: HireItemListStoreService
  ) {}

  ngOnInit() {
    this.route.params
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((params) => {
        let id = params['docketId'];
        this.docketId.next(Number(id));
        this.getDocket(id);
      });

    this.refreshDataDependentOnHireItems$
      .pipe(
        skip(1), // skip the initial value, as this will cause a double call to getDocket
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.getDocket(this.docket.id);
      });

    this.registerConfigSubscribes();

    this.possibleStatusUpdate
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.checkStatus();
      });

    this.router.events
      .pipe(filter((event: any) => event instanceof NavigationStart))
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.dialog.closeAll();
      });
  }

  private registerConfigSubscribes() {
    this.configService.currentLocation
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((location) => {
        this.currentLocation = location;
      });
    this.configService.currentUser
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((user) => {
        this.currentUser = user;
      });
    this.configService.userConfig
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((config) => {
        if ('currency' in config) {
          this.defaultCurrency = config['currency'];
        }
      });
  }

  setStatusHeading() {
    if (this.docket.account_reference) {
      this.statusHeading = 'Hire Invoice';
    } else {
      this.statusHeading = 'Hire Contract';
    }
  }

  getDocket(id) {
    this.docketService
      .getDocket(id, ['customer', 'comments.user'])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((response) => {
        this.salesOrderLoading = false;
        this.docket = response as Docket;
        this.taxRate$.next(this.docket.tax_rate);
        console.log(this.docket.closed && this.docket.account_reference);
        this.isInvoiceAndClosed.next(
          Boolean(this.docket.closed && this.docket.account_reference)
        );
        this.setStatusHeading();
      });
  }

  editSettings() {
    const config = {
      data: {
        docket: this.docket,
        confirmInvoiceDetails: false
      }
    };
    this.dialog
      .open(DocketSettingsDialogComponent, config)
      .afterClosed()
      .pipe(takeUntil(this.ngUnsubscribe), takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.possibleStatusUpdate.next();
      });
  }

  changeCustomer() {
    this.dialog
      .open(UpdateCustomerDialogComponent, {
        data: {
          customer: this.docket.customer,
          docketType: 'hire'
        }
      })
      .afterClosed()
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter(Boolean),
        mergeMap((customer) => {
          return this.docketService.update(this.docket.id, {
            customer_id: customer.id
          });
        })
      )
      .subscribe(() => this.getDocket(this.docket.id));
  }

  print(download = false) {
    this.docketService
      .print2(this.docket.id, this.currentLocation.name)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((response) => {
        let src = URL.createObjectURL(
          base64ToBlob(response, 'application/pdf')
        );
        if (download) {
          const link = document.createElement('a') as HTMLAnchorElement;
          link.href = src;
          const ref = this.docket.account_reference
            ? this.docket.account_reference
            : this.docket.id;
          link.download =
            ref +
            ' (' +
            this.customerNamePipe.transform(this.docket.customer) +
            ')';
          link.click();
        } else {
          this.printFrame.nativeElement.src = src;
          this.printIframe();
        }
      });
  }

  email() {
    this.dialog.open(EmailDocumentToCustomerDialogComponent, {
      data: {
        documentType: 'hire',
        documentId: this.docket.id,
        docket: this.docket,
        customer: this.docket.customer
      },
      viewContainerRef: this.viewContainerRef
    });
  }

  printIframe() {
    observableTimer(500)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.printFrame.nativeElement.contentWindow.print();
      });
  }

  addAccountsReference() {
    if (this.docket.account_reference) {
      return;
    }
    this.isDocketLocked$
      .pipe(
        take(1),
        mergeMap((isLocked) => {
          if (isLocked && !this.docket.closed) {
            this.toastrService.error(
              'You must off hire all items before you can convert this to an invoice, or use the rollover feature to create a new hire contract and convert this one to an invoice.',
              'Error',
              {
                timeOut: 30_000
              }
            );
            return of(null);
          }
          return this.dialog
            .open(DocketSettingsDialogComponent, {
              data: { docket: this.docket, confirmInvoiceDetails: true }
            })
            .afterClosed()
            .pipe(
              take(1),
              map((result) => {
                if (result && result.convertToInvoice) {
                  return true;
                }
                return false;
              })
            );
        }),
        filter((convertToInvoice) => Boolean(convertToInvoice)),
        mergeMap(() => this.docketService.addSalesOrder(this.docket.id)),
        tap(() => this.checkStatus()) // check status to force chargeables to update
      )
      .subscribe(() => this.getDocket(this.docket.id));
  }

  updateHireItems(newHireItems: HireItem[]) {
    this.hireItems = newHireItems;
    this.hireItemsOfTypeItem = newHireItems.flatMap((item) =>
      item.hireable_type === 'product' ? item.children : [item]
    );
    this.hireItems$.next(newHireItems);
    this.possibleStatusUpdate.next();
  }

  onDeliveriesUpdated(deliveryListItems: DeliveryListItem[]) {
    this.possibleStatusUpdate.next();
    this.deliveryChargeSource.next(deliveryListItems);
  }

  openPaymentDialog() {
    this.priceContainer.viewAddPaymentDialog();
  }

  toggleShowConsumables() {
    this.showConsumables = true;
  }

  newPayment() {
    this.possibleStatusUpdate.next();
  }

  markAsTransferred() {
    this.confirmDialogRef = this.dialog.open(ConfirmDialogComponent);
    this.confirmDialogRef.componentInstance.message =
      'Are you sure you want to mark this docket as transferred?';
    this.confirmDialogRef.componentInstance.title = 'Transfer Docket';
    this.confirmDialogRef.componentInstance.type = 'transfer';
    this.confirmDialogRef
      .afterClosed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.docketService
            .update(this.docket.id, { status: 'Transferred' })
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(
              () => {
                this.possibleStatusUpdate.next();
              },
              () => {
                this.possibleStatusUpdate.next();
              }
            );
        }
      });
  }

  updateDocketContact(contactId: number | null) {
    this.docketService
      .update(this.docket.id, { contact_id: contactId })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe();
  }

  showRoundDialog() {
    this.isDocketLocked$
      .pipe(
        take(1),
        mergeMap((isLocked) => {
          if (isLocked) {
            this.toastrService.error('You cannot round a locked docket');
            return of(false);
          }
          return this.dialog
            .open(RoundPriceDialogComponent, {
              data: {
                total: this.priceContainer.getTotalAmount(),
                hireItems: this.hireItems,
                docket: this.docket
              }
            })
            .afterClosed()
            .pipe(take(1));
        })
      )
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((result) => {
        if (result) {
          this.checkStatus();
          this.hireItemList.reloadHireItems();
        }
      });
  }

  editDiscount() {
    this.isDocketLocked$
      .pipe(
        take(1),
        mergeMap((isLocked) => {
          if (isLocked) {
            this.toastrService.error(
              'You cannot edit the discount on a locked docket'
            );
            return of(false);
          }
          return this.dialog
            .open(DiscountDialogComponent, {
              data: {
                totalExDiscount: this.priceContainer.getTotalExDiscountAmount(),
                docket: this.docket
              },
              viewContainerRef: this.viewContainerRef
            })
            .afterClosed()
            .pipe(take(1));
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.checkStatus();
      });
  }

  private checkStatus() {
    this.docketService
      .getStatus(this.docket.id)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((result) => {
        if (this.docket?.rolled_over_to !== result.rolled_over_to) {
          document.location.reload();
          return;
        }
        this.docket.status = result.status;
        this.docket.rollover_date = result.rollover_date;
        this.docket.rolled_over_to = result.rolled_over_to;
        this.docket.currency_rate = result.currency_rate;
        this.docket.discount = result.discount;
        this.taxRate$.next(result.tax_rate);
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private retailItemToChargeable({
    tax_rate,
    quantity,
    unit_price
  }: RetailItemSale): Chargeable {
    return {
      tax_rate,
      charge: quantity * unit_price
    };
  }

  private hireItemToChargeable(
    item: HireItem,
    docketTaxRate: number
  ): Chargeable {
    return {
      tax_rate: docketTaxRate,
      charge: item.charge * 100
    };
  }

  private deliveryToChargeable(delivery: DeliveryListItem, taxRate: number) {
    return {
      tax_rate: taxRate,
      charge: delivery.charge * 100
    };
  }
}
